import { useCallback, useContext, useRef } from 'react'

import { AxiosError } from 'axios'
import useSWR, { cache, useSWRInfinite } from 'swr'

import { fetchPagedData, MetricsApi, PagedResponse } from 'api'
import { DateRange } from 'components/DateRangePicker'
import { CurrentUserContext } from 'context/currentUserContext'
import { AccountActivitySortValue, Insight } from 'modules/accountActivity/domain'
import { captureException } from 'utils/errors'
import { FilterPage } from 'utils/filters'

import { getCurrentState, PagedResponseData, useTransformedFilters } from './paginationAndApiUtils'

const DEFAULT_PAGE_SIZE = 25
const LEGACY_INSIGHT_TYPES = ['ACTIVITY_GROUP_BY_ACCOUNT_AND_END_USERS']

let loadTimeMetricSent = false

const fetcherFn = (
  url: string,
  sortBy: AccountActivitySortValue,
  descending: boolean,
  searchQuery: string | undefined,
  dateRange: DateRange | undefined,
  stringifiedFilterGroups: string,
  accountId?: string,
  contactId?: string,
  insightTypes?: string[]
) =>
  fetchPagedData<Insight>({
    url,
    sortBy,
    descending,
    accountName: searchQuery,
    dateRange,
    filterGroups: JSON.parse(stringifiedFilterGroups),
    accountId,
    contactId,
    insightTypes
  })

const fetchFeed = async (
  url: string,
  sortBy: AccountActivitySortValue,
  descending: boolean,
  searchQuery: string | undefined,
  dateRange: DateRange | undefined,
  stringifiedFilterGroups: string,
  accountId?: string,
  contactId?: string,
  insightTypes?: string[]
) => {
  return fetcherFn(
    url,
    sortBy,
    descending,
    searchQuery,
    dateRange,
    stringifiedFilterGroups,
    accountId,
    contactId,
    insightTypes
  )
}

export const useInsightsFeed = (
  filterPage: FilterPage,
  selectedSort: AccountActivitySortValue,
  descending: boolean,
  pageSize: number = DEFAULT_PAGE_SIZE,
  withDateRange = true,
  legacyInsightsOnly = false,
  withFilters = true,
  accountId?: string,
  contactId?: string,
  customDateRange?: DateRange
): {
  insights: PagedResponseData<Insight>
  count: number | undefined
  refresh: () => void
} => {
  const currentUser = useContext(CurrentUserContext)
  const { searchQuery, dateRange, serializableFilterGroups } = useTransformedFilters(
    filterPage,
    currentUser?.id,
    customDateRange
  )

  const insightTypes = legacyInsightsOnly ? LEGACY_INSIGHT_TYPES : undefined

  const getKey = (index: number, previousPageData: PagedResponse<Insight> | null) => {
    if (previousPageData && !previousPageData.nextPagePath) {
      return null
    }

    return [
      previousPageData?.nextPagePath ??
        `/v2/insights/feed/?pageSize=${pageSize}&includeCount=false&includeOpps=false&includeScores=true`,
      selectedSort,
      descending,
      searchQuery,
      withDateRange ? dateRange : undefined,
      withFilters ? JSON.stringify(serializableFilterGroups) : '[]',
      accountId,
      contactId,
      insightTypes,
      `$$FILTER_PAGE=${filterPage}$$`
    ]
  }

  const countKey = [
    '/v2/insights/count/',
    AccountActivitySortValue.MOST_RECENT, // Sorting doesn't matter when getting the count
    true, // Same thing with sort direction
    searchQuery,
    withDateRange ? dateRange : undefined,
    JSON.stringify(serializableFilterGroups),
    accountId,
    contactId,
    insightTypes,
    `$$FILTER_PAGE=${filterPage}$$`
  ]

  const config = {
    revalidateOnMount: !cache.get(getKey(0, null)),
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  }

  const timeOfInitialRender = useRef(window.performance?.now())

  const extendedConfig = {
    ...config,
    loadingTimeout: 30_000,
    onSuccess: () => {
      if (!loadTimeMetricSent) {
        MetricsApi.post('/monitoring/metrics/add', {
          value: window.performance.now() - timeOfInitialRender.current,
          metric: 'activity-groups-first-rows-displayed',
          dimensions: {
            type: 'bucket',
            buckets: '500,1000,2000,4000,8000'
          }
        })

        loadTimeMetricSent = true
      }
    },
    onLoadingSlow: () => {
      MetricsApi.post('/monitoring/metrics/add', {
        metric: `activity-groups-loading-slow`,
        value: 1,
        dimensions: {
          type: 'counter'
        }
      })
    },
    onError: (error: any) => {
      captureException(error, {
        tags: { url: window.location.href },
        user: { id: `${currentUser?.id}` }
      })
      MetricsApi.post('/monitoring/metrics/add', {
        metric: `activity-groups-error`,
        value: 1,
        dimensions: {
          type: 'counter'
        }
      })
    }
  }

  const { data, error, mutate, size, setSize } = useSWRInfinite<PagedResponse<Insight>>(
    getKey,
    fetchFeed,
    filterPage === FilterPage.ACTIVITIES ? extendedConfig : config
  )

  const insightsCountSWR = useSWR<number | undefined, AxiosError>(
    countKey,
    fetcherFn as any,
    config
  )
  const insightsCount = insightsCountSWR.data
  const mutateCount = insightsCountSWR.mutate

  // WORKAROUND: Remove any duplicate rows. This can happen because groups are
  // processed in real time (and are sorted by recent activity). A set of new
  // groups can push the offsets of existing ones down onto a subsequent page
  // of results.
  const dedupedData: PagedResponse<Insight>[] | undefined = data ? [] : undefined
  const seen: Set<number> = new Set()

  if (data && dedupedData) {
    for (const page of data) {
      const dedupedPageData = []

      for (const activityGroup of page.data) {
        if (!seen.has(activityGroup.id)) {
          seen.add(activityGroup.id)
          dedupedPageData.push(activityGroup)
        }
      }

      dedupedData.push({ ...page, data: dedupedPageData })
    }
  }

  const refresh = useCallback(() => {
    cache
      .keys()
      .filter(key => key.includes(`$$FILTER_PAGE=${filterPage}$$`))
      .forEach(key => cache.delete(key))

    // only reload the first page in the event that the user has scrolled
    if (setSize) {
      setSize(1)
    }

    // forces revalidation (refetching) of data + count
    mutate(null as any, true)
    mutateCount(null as any, true)
  }, [filterPage, mutate, mutateCount, setSize])

  return {
    insights: {
      ...getCurrentState<Insight>(dedupedData, error, size),
      error,
      mutate,
      size,
      setSize
    },
    count: insightsCount,
    refresh
  }
}

export const useRefreshingInsightsCount = (
  filterPage: FilterPage,
  withDateRange = true,
  legacyInsightsOnly = false,
  accountId?: string,
  contactId?: string
): number | undefined => {
  const currentUser = useContext(CurrentUserContext)
  const { searchQuery, dateRange, serializableFilterGroups } = useTransformedFilters(
    filterPage,
    currentUser?.id
  )
  const insightTypes = legacyInsightsOnly ? LEGACY_INSIGHT_TYPES : undefined

  const key = [
    '/v2/insights/count/',
    AccountActivitySortValue.MOST_RECENT, // Sorting doesn't matter when getting the count
    true, // Same thing with sort direction
    searchQuery,
    withDateRange ? dateRange : undefined,
    JSON.stringify(serializableFilterGroups),
    accountId,
    contactId,
    insightTypes
  ]
  const config = { refreshInterval: 30000, revalidateOnMount: false }
  const { data } = useSWR<number | undefined, AxiosError>(key, fetcherFn as any, config)

  return data
}
